import { useState, useEffect, ChangeEvent, SyntheticEvent, useMemo } from 'react';
import {
  useTheme, Box, Grid, Card, Button, Divider, Typography, CardContent, useMediaQuery
} from '@mui/material';
import { NavLink, useParams } from 'react-router-dom';
import { Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import Title from '../../../components/Title';
import EditarModal from './modal/editpay'; // Ajustar la ruta según la ubicación real
import { Pay } from '../../../models/pay';
import theme from '../../../theme';
import { Collect } from '../../../models/collect';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow, SimpleColumnGroup, RowId } from '../../../components/table/interface';
import SelectCollectbyOffice from '../../../components/filter/SelectCollectbyOffice';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';
import moment from "moment-timezone";
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const EditPay: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentDate = nowOperationalDateString();

  const [office_id, setOffice_id] = useState('');
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [lends, setLends] = useState([]);
  const [marcado, setMarcado] = useState('');
  const [pays, setPays] = useState([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [offices, setOffices] = useState([]);
  const [openEditar, setOpenEditar] = useState(false);
  const [selectedPayId, setSelectedPayId] = useState('');
  const { id } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [start_date] = useState(currentDate);
  const [end_date] = useState(currentDate);
  const { drawerOpen } = useDrawer();
  const { canUpdate, canDelete } = useResourcePermissions('/prestamos/modificarabono');


  useEffect(() => {
    const fetchOffices = async () => {
      try {
        showLoading();
        const response = await axios.get('offices');
        setOffices(response.data.data);
        showAlert('Se ha realizado la consulta exitosamente', 'success');
      } catch (error) {
        showAlert('Error al cargar las oficinas', 'error');
        console.error('Error fetching offices:', error);
      } finally {
        hideLoading();
      }
    };

    fetchOffices();
  }, []);

  useEffect(() => {
    const fetchCollects = async () => {
      if (office_id) {
        try {
          const response = await axios.get(`collectsoffice/${office_id}`);
          setCollectsOffice(response.data);
          setSelectedCollects([]);
          setSelectAll(false);
          setLends([]);
          setPays([]);
        } catch (error) {
          console.error('Error fetching collects:', error);
        }
      }
    };

    fetchCollects();
  }, [office_id]);

  useEffect(() => {
    const fetchLends = async () => {
      if (marcado) {
        try {
          const response = await axios.get(`lendcollect/${marcado}`);
          setLends(response.data.data);
        } catch (error) {
          console.error('Error fetching lends:', error);
        }
      }
    };

    fetchLends();
  }, [marcado]);

  const submit = async (event?: SyntheticEvent, formData?: any) => {
    if (event) {
      event.preventDefault();
    }
    if (!office_id || !start_date || !end_date || selectedCollects.length === 0) {
      showAlert('Faltan datos por completar', 'warning');
      return;
    }

    if (!formData) {
      if (!office_id || !start_date || !end_date || selectedCollects.length === 0) {
        showAlert('Faltan datos por completar', 'warning');
        return;
      }
      formData = {
        end_date,
        start_date,
        collect_id: selectedCollects,
      };
    }
    try {
      showLoading();
      const response = await axios.post('/payscollect', formData);
      const data = response.data.data;
      setPays(data);
      if (data.length === 0) {
        showAlert('No se encontraron registros!', 'info');
      } else {
        showAlert('Consulta exitosa!', 'success');
      }
    } catch (error) {
      showAlert('Error al consultar los pagos', 'error');
      console.error('Error fetching data:', error);
    } finally {
      hideLoading();
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenModal = (id: string | undefined) => {
    setSelectedPayId(id ?? '');
    setModalOpen(true);
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedCollects((prevSelectedCollects) => {
      if (prevSelectedCollects.includes(value)) {
        return prevSelectedCollects.filter((id) => id !== value);
      } else {
        setMarcado(value);
        return [...prevSelectedCollects, value];
      }
    });
  };

  const handleOpenEditar = (payId: string) => {
    const selectedPay = pays.find((pay: Pay) => pay.id === parseInt(payId, 10));
    setSelectedPayId(payId);
    setOpenEditar(true);
  };

  const handleCloseEditar = () => {
    setOpenEditar(false)
    submit(undefined, undefined);
  };

  const handleDeletePay = async (payIdValue: number | string | null | undefined) => {
    const payId = Number(payIdValue);
    if (Number.isNaN(payId)) {
      showAlert('ID de abono inválido.', 'warning');
      return;
    }

    if (!window.confirm('¿Está seguro de eliminar este abono?')) {
      return;
    }

    try {
      showLoading();
      await axios.post('/pays/delete', { id: payId });
      showAlert('Abono eliminado correctamente.', 'success');
      await submit();
    } catch (error) {
      console.error('Error eliminando abono:', error);
      showAlert('Error al eliminar el abono.', 'error');
    } finally {
      hideLoading();
    }
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = collectsOffice.map(collect => collect.id.toString());
      setSelectedCollects(allIds);
      setSelectAll(true);
    } else {
      setSelectedCollects([]);
      setSelectAll(false);
    }
  };
  const decimalFormatter = useMemo(() => new Intl.NumberFormat('en-US', { style: 'decimal' }), []);

  const formatTime = (value: string | number) => {
    if (!value) return '';
    const m = moment(value);
    if (!m.isValid()) return '';
    return m.tz('America/Bogota').format('HH:mm:ss');
  };

  const fullColumns: SimpleColumn[] = [
    { field: 'collect', headerName: 'Cobro', minWidth: 150, wrapText: true },
    { field: 'id_client', headerName: 'Cédula', minWidth: 150 },
    { field: 'fullname', headerName: 'Cliente', minWidth: 200, maxWidth: 200, wrapText: true },
    {
      field: 'value',
      headerName: 'Abono',
      minWidth: 70,
      maxWidth: 70,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) => decimalFormatter.format(Number(row.value ?? 0)),
    },
    {
      field: 'balance',
      headerName: 'Saldo',
      minWidth: 90,
      maxWidth: 90,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) => decimalFormatter.format(Number(row.balance ?? 0)),
    },
    {
      field: 'quote',
      headerName: 'Cuota',
      minWidth: 90,
      maxWidth: 90,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) => decimalFormatter.format(Number(row.quote ?? 0)),
    },
    {
      field: 'time',
      headerName: 'Hora',
      minWidth: 100,
      renderCell: ({ row }: { row: SimpleRow }) => formatTime(String(row.time ?? '')),
    },
    {
      field: 'opt_edit',
      headerName: '',
      minWidth: '55px',
      maxWidth: '55px',
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => (
        <PermissionButton
          allowed={canUpdate}
          deniedTooltip="Sin permiso para editar abonos"
          tooltip="Editar"
          variant="contained"
          color="primary"
          onClick={() => handleOpenEditar(String(row.payId ?? ''))}
          sx={{
            minWidth: 'unset',
            width: '30px',
            padding: '3px',
            boxSizing: 'border-box',
          }}
        >
          <EditIcon />
        </PermissionButton>
      ),
    },
    {
      field: 'opt_delete',
      headerName: '',
      minWidth: '55px',
      maxWidth: '55px',
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => (
        <PermissionButton
          allowed={canDelete}
          deniedTooltip="Sin permiso para eliminar abonos"
          tooltip="Eliminar"
          variant="contained"
          color="error"
          onClick={() => handleDeletePay(row.payId)}
          sx={{
            minWidth: 'unset',
            width: '30px',
            padding: '3px',
            boxSizing: 'border-box',
          }}
        >
          <DeleteIcon />
        </PermissionButton>
      ),
    },
  ];

  const mobileColumns: SimpleColumn[] = [
    fullColumns[0],
    fullColumns[2],
    fullColumns[3],
    fullColumns[4],
    fullColumns[6],
    fullColumns[7],
    fullColumns[8],
  ];

  const rows: SimpleRow[] = [...pays]
    .sort((a: Pay, b: Pay) => {
      const collectCompare = String(a.lend.collect.name || '').localeCompare(
        String(b.lend.collect.name || ''),
        'es',
        { sensitivity: 'base' }
      );
      if (collectCompare !== 0) return collectCompare;

      const aDate = a.date_operation || a.pay_date;
      const bDate = b.date_operation || b.pay_date;
      return moment(bDate).valueOf() - moment(aDate).valueOf();
    })
    .map((pay: Pay) => ({
      id: pay.id as RowId,
      payId: pay.id,
      collect: pay.lend.collect.name,
      id_client: pay.lend.client.idnumber,
      fullname: pay.lend.client.fullname,
      value: pay.value,
      balance: pay.balance,
      quote: pay.lend.daily_pay_value,
      time: pay.date_operation || pay.pay_date,
    }));

  const displayColumns = isMobile ? mobileColumns : fullColumns;
  const columnGroups: SimpleColumnGroup[] = [
    { header: 'Opciones', fields: ['opt_edit', 'opt_delete'] },
  ];
  const displayColumnGroups = isMobile
    ? columnGroups.map(group => ({
      header: group.header,
      fields: group.fields.filter(field => mobileColumns.some(col => col.field === field)),
    })).filter(group => group.fields.length > 0)
    : columnGroups;

  return (
    <HeaderResponsive>
      <StickyListToolbar>
        <Title>Modificar Abonos</Title>
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="contained"
            size="small"
            onClick={submit}
            startIcon={isMobile ? null : <SearchIcon />}
          >
            {isMobile ? <SearchIcon /> : 'Buscar'}
          </Button>
        </Box>
      </StickyListToolbar>
      <Card
        sx={{
          width: (t) => stickyListRegionWidth(t, drawerOpen),
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
        }}
      >
        <CardContent>
          <SelectCollectbyOffice
            office_id={office_id}
            setOffice_id={setOffice_id}
            offices={offices}
            collectsOffice={collectsOffice}
            selectedCollects={selectedCollects}
            setSelectedCollects={setSelectedCollects}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            handleCheckboxChange={handleCheckboxChange}
            handleSelectAll={handleSelectAll}
          />
        </CardContent>

        <CardContent
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
          }}
        >
          {pays.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={displayColumns}
              maxHeight={600}
              showBorders
              showCount
              exportCsv
              exportFileName="modificar-abonos.csv"
              wrapCellContent
              columnGroups={displayColumnGroups}
            />
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>
      <EditarModal
        isOpen={openEditar}
        onClose={handleCloseEditar}
        id={selectedPayId ?? ''}
        onButtonClick={() => {
          handleCloseEditar();
        }}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </HeaderResponsive>
  );
};

export default EditPay;
